/**
 * The empty state every hierarchy list shares (f4.1's, generalised by f9.2).
 *
 * f4.1 shipped this as "two distinct empty states, because on day one of
 * phase 4 BOTH are true", and branched on `mayManage` -- a WRITE permission
 * standing in for "can this caller see everything". That was the best
 * predicate available before f9.1. The honest one is `scope.is_global`, from
 * the server, and the branch now turns on it: a DM holds a write grant and
 * sees three clients, so "No clients yet -- create the first one" would be
 * false for them.
 *
 * Chosen from server facts alone, in this order:
 *
 * | State | When |
 * | --- | --- |
 * | `No … match` | the caller's own search narrowed a non-empty set to zero |
 * | `No parent … exists yet` | `scope.is_global`, and this collection AND its parent collection are empty |
 * | `No … yet` | `scope.is_global` -- a fresh install before the phase-8 import |
 * | `Nothing in your scope yet` | anyone else |
 *
 * The parent state is reserved for a global caller because only for them does
 * an empty parent collection mean that no parent EXISTS. For a scoped caller
 * it means none is theirs, and saying otherwise would be false.
 *
 * `is_global` selects COPY here and nothing else: no control, no entry and no
 * route appears or disappears because of it. No count, facet or "0 of N" is
 * rendered, and no node is named.
 */

import type { ReactNode } from 'react';
import { Search } from 'lucide-react';

import { useSessionScope } from '@/session/SessionScopeContext';

export interface ScopedEmptyStateProps {
  /** The caller's own search narrowed the loaded set to nothing. */
  searching: boolean;
  /** e.g. `groups`, for "No groups match". */
  matchNoun: string;
  /** e.g. `Business Unit Groups`, for "No Business Unit Groups yet". */
  collectionNoun: string;
  icon: ReactNode;
  /** What a global caller does next on a fresh install. */
  freshInstallHint: string;
  /**
   * Set when the parent collection is ALSO empty, e.g. no Business Unit Group
   * exists for a Business Unit to hang off.
   */
  parentMissing?: { title: string; hint: ReactNode } | null;
}

export function ScopedEmptyState({
  searching,
  matchNoun,
  collectionNoun,
  icon,
  freshInstallHint,
  parentMissing = null,
}: ScopedEmptyStateProps) {
  const { session } = useSessionScope();

  if (searching) {
    return (
      <EmptyBody icon={<Search aria-hidden="true" />} title={`No ${matchNoun} match`}>
        Clear the search or the filters to see the rest.
      </EmptyBody>
    );
  }
  if (session?.scope?.is_global) {
    if (parentMissing) {
      return (
        <EmptyBody icon={icon} title={parentMissing.title}>
          {parentMissing.hint}
        </EmptyBody>
      );
    }
    return (
      <EmptyBody icon={icon} title={`No ${collectionNoun} yet`}>
        {freshInstallHint}
      </EmptyBody>
    );
  }
  return (
    <EmptyBody icon={icon} title="Nothing in your scope yet">
      Nothing here is part of the hierarchy you are named on. An administrator, or the
      next master workbook import, can put you in scope.
    </EmptyBody>
  );
}

function EmptyBody({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="e-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
